import constate from 'constate';
import { KeyboardEvent, useCallback } from 'react';

import { useFormContext } from 'core/states';
import { PricingHelper } from 'pricing/helpers';
import { PriceBasedOn } from 'quotes/enums';
import { ICustomerQuote, IQuotePartPriceData } from 'quotes/types';

interface IPriceFormFieldsProps {
  quote?: ICustomerQuote;
  priceData: IQuotePartPriceData;
  priceDecimals: number;
  onPriceChange?: (price: number) => void;
  onPriceKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onMarginChange?: (margin: number) => void;
  alternateVendorPartNumber?: string;
  costFieldName: string;
  marginFieldName: string;
  replacementCostFieldName?: string;
  priceBasedOnFieldName?: string;
  disabledFields?: boolean;
}

const usePriceFormFieldsState = ({
  quote,
  priceData,
  priceDecimals,
  onPriceChange,
  onPriceKeyDown,
  onMarginChange,
  alternateVendorPartNumber,
  costFieldName,
  marginFieldName,
  replacementCostFieldName,
  priceBasedOnFieldName = 'priceBasedOn',
  disabledFields: externalDisabledFields = false,
}: IPriceFormFieldsProps) => {
  const { getValues, setValue, watch } = useFormContext();

  const standardCost = watch(costFieldName);
  const replacementStandardCost = replacementCostFieldName ? watch(replacementCostFieldName) : null;
  const priceBasedOn = watch(priceBasedOnFieldName) || PriceBasedOn.QUOTED;

  const activeCost =
    priceBasedOn === PriceBasedOn.REPLACEMENT ? replacementStandardCost : standardCost;

  const disabledFields = priceData?.priceLocked || externalDisabledFields;

  // User manually types a price — recalculate both margins
  const handleOnChangePrice = useCallback(
    ({ source, target: { value: price } }) => {
      if (source !== 'event') {
        return;
      }

      const quotedCost = getValues(costFieldName);

      // True margin: from quoted cost
      setValue(marginFieldName, PricingHelper.computeGpmPercentage(quotedCost, price));

      // Pricing margin: from active cost
      if (replacementCostFieldName) {
        const currentPriceBasedOn = getValues(priceBasedOnFieldName) || PriceBasedOn.QUOTED;
        const repCost = getValues(replacementCostFieldName);
        const pricingCost =
          currentPriceBasedOn === PriceBasedOn.REPLACEMENT && repCost ? repCost : quotedCost;

        setValue('pricingMargin', PricingHelper.computeGpmPercentage(pricingCost, price));
      }

      onPriceChange?.(price);
    },
    [
      costFieldName,
      marginFieldName,
      priceBasedOnFieldName,
      replacementCostFieldName,
      getValues,
      setValue,
      onPriceChange,
    ]
  );

  // User edits the pricing margin — recalculate price and true margin
  const handleOnChangeMargin = useCallback(
    ({ source, target: { value } }) => {
      if (source !== 'event') {
        return;
      }

      // Compute price from active cost × margin
      const currentPriceBasedOn = getValues(priceBasedOnFieldName) || PriceBasedOn.QUOTED;
      const quotedCost = getValues(costFieldName);
      const repCost = replacementCostFieldName ? getValues(replacementCostFieldName) : null;
      const cost =
        currentPriceBasedOn === PriceBasedOn.REPLACEMENT && repCost ? repCost : quotedCost;

      const price =
        cost && value != null
          ? PricingHelper.computeAndFormatPrice(cost, value / 100, { decimals: priceDecimals })
          : null;

      setValue('price', price);
      setValue('pricingMargin', value);
      // Derive true margin from quoted cost
      if (price && quotedCost) {
        setValue(marginFieldName, PricingHelper.computeGpmPercentage(quotedCost, price));
      }

      onPriceChange?.(price);
      onMarginChange?.(value);
    },
    [
      costFieldName,
      marginFieldName,
      priceBasedOnFieldName,
      priceDecimals,
      replacementCostFieldName,
      getValues,
      setValue,
      onPriceChange,
      onMarginChange,
    ]
  );

  return {
    priceData,
    priceDecimals,
    disabledFields,
    standardCost,
    replacementStandardCost,
    activeCost,
    priceBasedOn,
    marginFieldName,
    onChangePrice: handleOnChangePrice,
    onChangeMargin: handleOnChangeMargin,
    alternateVendorPartNumber,
    quote,
    onPriceKeyDown,
  };
};

export const [PriceFormFieldsProvider, usePriceFormFieldsContext] =
  constate(usePriceFormFieldsState);
